/**
 * The filter drawer every Call Report uses.
 *
 * One drawer, configured by which `fields` a report declares, rather than four
 * drawers that each drift a little: the window (timezone, interval, dates and
 * TIMES) is identical across all four, and it is the part that must behave the
 * same everywhere — an operator comparing the inbound report against call
 * history has to know both were asked the same question.
 *
 * The window is a date AND time range because call volume is a shift question
 * ("what happened between 17:00 and 20:00 yesterday"), and a whole-day window
 * buries exactly the peak being investigated. Times are optional; empty means
 * whole days.
 *
 * Layout, validation and the Reset affordance are lifted from the conversation
 * report drawers so the whole Reports section reads as one product.
 */
import { useEffect, useMemo, useState, type JSX, type ReactNode } from 'react'
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'
import { Calendar } from 'primereact/calendar'
import { InputText } from 'primereact/inputtext'
import { Checkbox } from 'primereact/checkbox'
import { RotateCcw, SlidersHorizontal } from 'lucide-react'

import ResourceDrawer from '../../admin/ResourceDrawer.js'
import {
  FALLBACK_INTERVALS,
  useCallReportFilters,
} from '../../../hooks/useCallReportFilters.js'
import type { CallReportFilters as Filters } from '../../../hooks/useCallReport.js'
import { useOrgTimezone } from '../../../hooks/useOrgTimezone.js'

/**
 * The optional fields a report can ask for.
 *
 * Named after what the operator filters on, not after the query parameter, so
 * a report declares intent ("this one is about IVR") rather than plumbing.
 */
export type CallFilterField =
  | 'directions'
  | 'phone'
  | 'queues'
  | 'campaigns'
  | 'agents'
  | 'ivrMenus'
  | 'dispositions'
  | 'trunks'
  | 'digits'
  | 'includeNoSelection'

interface Props {
  open: boolean
  onClose: () => void
  onRun: (filters: Filters) => void
  onReset: () => void
  running: boolean
  /** Which optional filters this report offers, in the order shown. */
  fields: readonly CallFilterField[]
  /** One line under the header saying what the window is measured on. */
  windowNote: string
  /** Interval the drawer opens on. Hourly suits the interval reports. */
  defaultInterval?: string
}

/** The DTMF keys a menu can offer, as a picker rather than free text. */
const DIGIT_OPTIONS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'].map((d) => ({
  label: d,
  value: d,
}))

function toDateString(d: Date | null): string {
  if (!d) return ''
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** `HH:MM` in local time; the API validates the same shape. */
function toTimeString(d: Date | null, fallback: string): string {
  if (!d) return fallback
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export default function CallReportFilters({
  open,
  onClose,
  onRun,
  onReset,
  running,
  fields,
  windowNote,
  defaultInterval = '1d',
}: Props): JSX.Element {
  const browserZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    [],
  )
  const { timezone: orgTimezone } = useOrgTimezone()

  // Start on the resolved timezone if it's already cached (no flash), or the
  // browser zone as a placeholder while the first fetch is in flight.
  const [timezone, setTimezone] = useState(orgTimezone || browserZone)

  // Once the resolved timezone arrives, adopt it — but only if the user hasn't
  // manually changed the dropdown yet (timezone still matches the initial value).
  useEffect(() => {
    if (orgTimezone) setTimezone(orgTimezone)
  }, [orgTimezone])
  const [interval, setInterval] = useState(defaultInterval)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [startTime, setStartTime] = useState<Date | null>(null)
  const [endTime, setEndTime] = useState<Date | null>(null)
  const [directions, setDirections] = useState<string[]>([])
  const [phone, setPhone] = useState('')
  const [queueIds, setQueueIds] = useState<string[]>([])
  const [campaignIds, setCampaignIds] = useState<string[]>([])
  const [agentIds, setAgentIds] = useState<string[]>([])
  const [ivrIds, setIvrIds] = useState<string[]>([])
  const [dispositionIds, setDispositionIds] = useState<string[]>([])
  const [trunkIds, setTrunkIds] = useState<string[]>([])
  const [digits, setDigits] = useState<string[]>([])
  const [includeNoSelection, setIncludeNoSelection] = useState(false)
  const [touched, setTouched] = useState(false)

  const { options, loading } = useCallReportFilters()

  const timezoneOptions = useMemo(() => {
    const list = options.timezones.map((t) => ({
      label: t.offset ? `${t.label} (${t.offset})` : t.label,
      value: t.value,
    }))
    // The browser's own zone is the one the operator is actually in; if the
    // catalog does not carry it, the drawer would otherwise open on a zone
    // they never chose.
    if (!list.some((t) => t.value === browserZone)) {
      list.unshift({ label: browserZone, value: browserZone })
    }
    return list
  }, [options.timezones, browserZone])

  const intervalOptions = options.intervalOptions.length > 0
    ? options.intervalOptions
    : FALLBACK_INTERVALS

  // A digit filter and "include visits with no selection" are contradictory;
  // the API resolves it in favour of the digits, and the drawer says so by
  // clearing the checkbox rather than leaving a setting that does nothing.
  useEffect(() => {
    if (digits.length > 0) setIncludeNoSelection(false)
  }, [digits])

  const errors = {
    startDate: !startDate ? 'Start date is required' : '',
    endDate: !endDate
      ? 'End date is required'
      : startDate && endDate < startDate
        ? 'End date must be on or after start date'
        : '',
  }
  const valid = !errors.startDate && !errors.endDate

  const has = (field: CallFilterField): boolean => fields.includes(field)

  const handleRun = (): void => {
    setTouched(true)
    if (!valid) return

    const extra: Record<string, string> = {}
    if (has('directions') && directions.length > 0) extra['directions'] = directions.join(',')
    if (has('phone') && phone.trim() !== '') extra['phone'] = phone.trim()
    if (has('queues') && queueIds.length > 0) extra['queueIds'] = queueIds.join(',')
    if (has('campaigns') && campaignIds.length > 0) extra['campaignIds'] = campaignIds.join(',')
    if (has('agents') && agentIds.length > 0) extra['agentIds'] = agentIds.join(',')
    if (has('ivrMenus') && ivrIds.length > 0) extra['ivrIds'] = ivrIds.join(',')
    if (has('dispositions') && dispositionIds.length > 0) {
      extra['dispositionIds'] = dispositionIds.join(',')
    }
    if (has('trunks') && trunkIds.length > 0) extra['trunkIds'] = trunkIds.join(',')
    if (has('digits') && digits.length > 0) extra['digits'] = digits.join(',')
    if (has('includeNoSelection') && includeNoSelection) extra['includeNoSelection'] = '1'

    onRun({
      timezone,
      interval,
      startDate: toDateString(startDate),
      endDate: toDateString(endDate),
      // Empty time inputs mean the whole day, which is the ordinary case.
      startTime: toTimeString(startTime, '00:00'),
      endTime: toTimeString(endTime, '23:59'),
      extra,
    })
  }

  const handleReset = (): void => {
    setTimezone(orgTimezone)
    setInterval(defaultInterval)
    setStartDate(null)
    setEndDate(null)
    setStartTime(null)
    setEndTime(null)
    setDirections([])
    setPhone('')
    setQueueIds([])
    setCampaignIds([])
    setAgentIds([])
    setIvrIds([])
    setDispositionIds([])
    setTrunkIds([])
    setDigits([])
    setIncludeNoSelection(false)
    setTouched(false)
    onReset()
  }

  const showError = (msg: string): JSX.Element | null =>
    touched && msg
      ? <small className="mt-1 block text-xs font-medium text-red-600">{msg}</small>
      : null

  const multi = (
    field: CallFilterField,
    label: string,
    value: string[],
    onChange: (next: string[]) => void,
    list: { label: string; value: string }[],
    noun: string,
  ): JSX.Element | null =>
    has(field) ? (
      <Field label={label} key={field}>
        <MultiSelect
          value={value}
          options={list}
          onChange={(e) => onChange((e.value as string[]) ?? [])}
          filter
          display="chip"
          maxSelectedLabels={2}
          selectedItemsLabel={`{0} ${noun}`}
          placeholder={loading ? 'Loading…' : `All ${noun}`}
          className="w-full"
        />
      </Field>
    ) : null

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      onSave={async () => { handleRun() }}
      saving={running}
      disabled={touched && !valid}
      width="xl"
      saveLabel={running ? 'Running…' : 'Run Report'}
      title="Report Filters"
    >
      <div className="kc3-form">
        <p className="mb-4 flex items-center gap-2 text-xs text-slate-500">
          <SlidersHorizontal className="h-3.5 w-3.5 text-[#3366FF]" aria-hidden="true" />
          Choose a window, then run the report. Times are optional — leave them empty for whole days.
        </p>

        <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.07em] text-slate-400">Reporting window</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Timezone">
            <Dropdown
              value={timezone}
              options={timezoneOptions}
              onChange={(e) => setTimezone(String(e.value))}
              filter
              placeholder="Select timezone"
              className="w-full"
            />
          </Field>

          <Field label="Interval Type" required>
            <Dropdown
              value={interval}
              options={intervalOptions}
              onChange={(e) => setInterval(String(e.value))}
              className="w-full"
            />
          </Field>

          <Field label="Start Date" required>
            <Calendar
              value={startDate}
              onChange={(e) => setStartDate((e.value as Date | null) ?? null)}
              dateFormat="yy-mm-dd"
              showIcon
              placeholder="Select start date"
              className={`w-full ${touched && errors.startDate ? 'p-invalid' : ''}`}
              maxDate={endDate ?? undefined}
            />
            {showError(errors.startDate)}
          </Field>

          <Field label="Start Time">
            <Calendar
              value={startTime}
              onChange={(e) => setStartTime((e.value as Date | null) ?? null)}
              timeOnly
              hourFormat="24"
              showIcon
              icon="pi pi-clock"
              placeholder="00:00"
              className="w-full"
            />
          </Field>

          <Field label="End Date" required>
            <Calendar
              value={endDate}
              onChange={(e) => setEndDate((e.value as Date | null) ?? null)}
              dateFormat="yy-mm-dd"
              showIcon
              placeholder="Select end date"
              className={`w-full ${touched && errors.endDate ? 'p-invalid' : ''}`}
              minDate={startDate ?? undefined}
            />
            {showError(errors.endDate)}
          </Field>

          <Field label="End Time">
            <Calendar
              value={endTime}
              onChange={(e) => setEndTime((e.value as Date | null) ?? null)}
              timeOnly
              hourFormat="24"
              showIcon
              icon="pi pi-clock"
              placeholder="23:59"
              className="w-full"
            />
          </Field>

        </div>

        <p className="mb-3 mt-5 border-t border-slate-100 pt-4 text-[11px] font-semibold uppercase tracking-[0.07em] text-slate-400">Filters</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {has('directions') && (
            <Field label="Call Type">
              <MultiSelect
                value={directions}
                options={options.directionOptions}
                onChange={(e) => setDirections((e.value as string[]) ?? [])}
                display="chip"
                placeholder="All call types"
                className="w-full"
              />
            </Field>
          )}

          {has('phone') && (
            <Field label="Phone Number">
              <InputText
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Any part of either number"
                className="w-full"
              />
            </Field>
          )}

          {multi('queues', 'Queue', queueIds, setQueueIds, options.queues, 'queues')}
          {multi('campaigns', 'Campaign', campaignIds, setCampaignIds, options.campaigns, 'campaigns')}
          {multi('agents', 'Agent', agentIds, setAgentIds, options.agents, 'agents')}
          {multi('ivrMenus', 'IVR', ivrIds, setIvrIds, options.ivrMenus, 'IVRs')}
          {multi('dispositions', 'Disposition', dispositionIds, setDispositionIds, options.dispositions, 'dispositions')}
          {multi('trunks', 'Trunk', trunkIds, setTrunkIds, options.trunks, 'trunks')}

          {has('digits') && (
            <Field label="IVR Option">
              <MultiSelect
                value={digits}
                options={DIGIT_OPTIONS}
                onChange={(e) => setDigits((e.value as string[]) ?? [])}
                display="chip"
                placeholder="Any key"
                className="w-full"
              />
            </Field>
          )}

          {has('includeNoSelection') && (
            <Field label="Callers Who Chose Nothing">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Checkbox
                  inputId="includeNoSelection"
                  checked={includeNoSelection}
                  disabled={digits.length > 0}
                  onChange={(e) => setIncludeNoSelection(e.checked === true)}
                />
                <span className={digits.length > 0 ? 'text-slate-400' : ''}>
                  Include IVR visits with no keypress
                </span>
              </label>
              {digits.length > 0 && (
                <small className="mt-1 block text-xs text-slate-400">
                  Not available while an IVR option is selected.
                </small>
              )}
            </Field>
          )}
        </div>

        <div className="mt-5 flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
          <p className="text-xs text-slate-500">{windowNote}</p>
          <button
            type="button"
            onClick={handleReset}
            className="inline-flex items-center gap-1.5 rounded-[3px] border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-all hover:border-slate-300 hover:bg-slate-50"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            Reset
          </button>
        </div>
      </div>
    </ResourceDrawer>
  )
}

function Field({
  label,
  required = false,
  children,
}: { label: string; required?: boolean; children: ReactNode }): JSX.Element {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}
        {required && <span aria-hidden className="ml-1 text-red-500">*</span>}
      </label>
      {children}
    </div>
  )
}
